/** 支出欄位組（Capture 頁用；`ui-spec.md` §3 ④）—— 用戶只需要填幾個欄 */
import { useTranslation } from 'react-i18next';
import type { Meta } from '../lib/api';

export interface ExpenseDraft {
  entityId: string;
  txnDate: string;
  amount: string;
  expenseCode: string;
  supplier: string;
  purpose: string;
  paidBy: string;
  paymentMethod: string;
  accountId: string;
  projectId: string;
}

export const emptyDraft = (meta: Meta | null, remembered?: Partial<ExpenseDraft>): ExpenseDraft => ({
  entityId: remembered?.entityId ?? meta?.entities[0]?.id ?? '',
  txnDate: new Date().toISOString().slice(0, 10),
  amount: '',
  expenseCode: remembered?.expenseCode ?? '',
  supplier: '',
  purpose: '',
  paidBy: remembered?.paidBy ?? 'simon',
  paymentMethod: remembered?.paymentMethod ?? 'cash',
  accountId: remembered?.accountId ?? '',
  projectId: '',
});

export function ExpenseFields({
  meta, draft, onChange, disabled, machine,
}: {
  meta: Meta;
  draft: ExpenseDraft;
  onChange: (patch: Partial<ExpenseDraft>) => void;
  disabled?: boolean;
  /** 邊啲欄係「機器讀」（OCR）→ 顯示 🤖 提醒要核對 */
  machine?: ReadonlySet<string>;
}) {
  const { t, i18n } = useTranslation();
  const zh = String(i18n.resolvedLanguage ?? 'zh-HK').startsWith('zh') ;
  const accounts = meta.accounts.filter((a) => a.entity_id === draft.entityId);
  const needAccount = draft.paidBy === 'company';
  const mark = (field: string) => (machine?.has(field) ? ' 🤖' : '');

  return (
    <div className="fields">
      <label className="field">
        <span>{t('expense.amount')} *{mark('amount')}</span>
        <input
          inputMode="decimal" className="amount" value={draft.amount} disabled={disabled}
          onChange={(e) => onChange({ amount: e.target.value })} placeholder="0.00" required
        />
      </label>
      <label className="field">
        <span>{t('expense.date')} *{mark('txnDate')}</span>
        <input type="date" value={draft.txnDate} disabled={disabled} onChange={(e) => onChange({ txnDate: e.target.value })} required />
      </label>
      <label className="field">
        <span>{t('expense.category')} *{mark('expenseCode')}</span>
        <select value={draft.expenseCode} disabled={disabled} onChange={(e) => onChange({ expenseCode: e.target.value })} required>
          <option value="">—</option>
          {meta.expenseCategories.map((c) => (
            <option key={c.code} value={c.code}>{zh ? c.name_zh : c.name_en}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('expense.purpose')} *{mark('purpose')}</span>
        <input value={draft.purpose} disabled={disabled} onChange={(e) => onChange({ purpose: e.target.value })} placeholder={t('expense.purpose_hint')} required />
      </label>
      <label className="field">
        <span>{t('expense.supplier')}{mark('supplier')}</span>
        <input
          list="vendors-list" value={draft.supplier} disabled={disabled}
          onChange={(e) => onChange({ supplier: e.target.value })} placeholder={t('expense.supplier_hint')}
        />
        <datalist id="vendors-list">
          {meta.vendors.map((v) => <option key={v.id} value={v.name} />)}
        </datalist>
      </label>
      <label className="field">
        <span>{t('expense.entity')} *</span>
        <select value={draft.entityId} disabled={disabled} onChange={(e) => onChange({ entityId: e.target.value, accountId: '' })}>
          {meta.entities.map((e) => (
            <option key={e.id} value={e.id}>{e.name_zh}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('expense.paid_by')} *</span>
        <select value={draft.paidBy} disabled={disabled} onChange={(e) => onChange({ paidBy: e.target.value })}>
          <option value="simon">{t('expense.paid_by_simon')}</option>
          <option value="raymond">{t('expense.paid_by_raymond')}</option>
          <option value="company">{t('expense.paid_by_company')}</option>
        </select>
      </label>
      <label className="field">
        <span>{t('expense.payment_method')} *</span>
        <select value={draft.paymentMethod} disabled={disabled} onChange={(e) => onChange({ paymentMethod: e.target.value })}>
          {meta.paymentMethods.map((m) => (
            <option key={m} value={m}>{t(`method.${m}`)}</option>
          ))}
        </select>
      </label>
      {needAccount && (
        <label className="field">
          <span>{t('expense.account')} *</span>
          <select value={draft.accountId} disabled={disabled} onChange={(e) => onChange({ accountId: e.target.value })}>
            <option value="">—</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span>{t('expense.project')}</span>
        <select value={draft.projectId} disabled={disabled} onChange={(e) => onChange({ projectId: e.target.value })}>
          <option value="">—</option>
          {meta.projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
